import { MouseEvent, useMemo, useState } from 'react';
import { Typography } from '@mui/material';
import { useAsyncValue } from 'framework/utils';
import { Button } from 'framework/ui/mui-builder';
import { FieldProps, useFieldHelper } from 'framework/ui/field';
import { useOfficeService, OfficeType, usePositionService, PositionType } from 'domain/shared/employee';
import {
  AssignmentFormItem,
  COMPLIANCE_DOCUMENT_STATUS,
  ComplianceDocumentMetadataTypeFE,
  TARGET_AUDIENCE_ALL_EMPLOYEES,
  getTargetAudienceDictionaries,
  getTargetAudienceDisplayParts,
} from 'domain/shared/compliance';
import {
  ComplianceAudienceCoverageModal,
  getDocumentStatusView,
  useComplianceAudienceCoverageService,
} from './complianceAudienceCoverage';

type ComplianceDocumentTargetAudienceFieldProps = FieldProps & {
  isOutdated?: boolean;
  coverageAssignmentsSource?: string;
  displayAssignmentsSource?: string;
};

const ComplianceDocumentTargetAudienceField = ({
  source = 'metadata',
  isOutdated = false,
  coverageAssignmentsSource,
  displayAssignmentsSource,
}: ComplianceDocumentTargetAudienceFieldProps) => {
  const { getValue } = useFieldHelper<Nullable<ComplianceDocumentMetadataTypeFE>>({ source });
  const { getValue: getPath } = useFieldHelper<string>({ source: 'path' });
  const { getValue: getCoverageAssignments } = useFieldHelper<AssignmentFormItem[]>({
    source: coverageAssignmentsSource || 'metadata.assignments',
  });
  const { getValue: getDisplayAssignments } = useFieldHelper<AssignmentFormItem[]>({
    source: displayAssignmentsSource || 'metadata.assignments',
  });
  const { findAll: findAllOffices } = useOfficeService();
  const { findAll: findAllPositions } = usePositionService();
  const offices = useAsyncValue<OfficeType[]>(findAllOffices, []);
  const positions = useAsyncValue<PositionType[]>(findAllPositions, []);
  const [isModalOpen, setModalOpen] = useState(false);

  const {
    assignments = [],
    relatedEmployeeFilter,
    relatedEmployeeContractTypes,
    isSignatureRequired,
    complianceName,
    version,
    status,
  } = getValue() || {};
  const currentDocumentPath = getPath();
  const documentStatusView = getDocumentStatusView(status, { isOutdated });
  const coverageAssignments = getCoverageAssignments() || assignments;
  const displayAssignments = getDisplayAssignments() || assignments;

  const { loading, isLoadingMore, coverageEntries, hasMoreCoverageEntries, openCoverage, loadMoreCoverage } =
    useComplianceAudienceCoverageService({
      complianceName,
      currentDocumentPath,
      version,
      shouldLoadStatuses: documentStatusView.shouldLoadStatuses,
      useLegacyAudienceWorkers: status === COMPLIANCE_DOCUMENT_STATUS.UNPUBLISHED,
      relatedEmployeeFilter,
      relatedEmployeeContractTypes,
      assignments: coverageAssignments,
    });

  const dictionaries = useMemo(() => {
    const projectsById = Object.fromEntries(
      displayAssignments
        .filter(assignment => !assignment.account)
        .map(assignment => [String(assignment.id), assignment.name]),
    );
    const accountsById = Object.fromEntries(
      displayAssignments
        .filter(assignment => assignment.account)
        .map(assignment => [String(assignment.id), assignment.name]),
    );

    return {
      ...getTargetAudienceDictionaries(offices || [], positions || []),
      projectsById,
      accountsById,
    };
  }, [offices, positions, displayAssignments]);

  const targetAudienceParts = useMemo(
    () =>
      getTargetAudienceDisplayParts(
        {
          relatedEmployeeFilter,
          assignments: displayAssignments
            .filter(assignment => !assignment.account)
            .map(assignment => String(assignment.id)),
          accounts: displayAssignments
            .filter(assignment => assignment.account)
            .map(assignment => String(assignment.id)),
          contractType: relatedEmployeeContractTypes,
        },
        dictionaries,
      ),
    [displayAssignments, dictionaries, relatedEmployeeContractTypes, relatedEmployeeFilter],
  );

  const handleOpenCoverage = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setModalOpen(true);
    void openCoverage();
  };

  const renderAudienceSummary = () => {
    if (!targetAudienceParts.length) {
      return <Typography>{TARGET_AUDIENCE_ALL_EMPLOYEES}</Typography>;
    }

    return targetAudienceParts.map(({ key, label, values }) => (
      <Typography key={`${key}-${values.join('-')}`}>
        <Typography component="span" variant="subtitle1">
          {`${label}:`}
        </Typography>{' '}
        {values.join(', ')};
      </Typography>
    ));
  };

  return (
    <>
      {renderAudienceSummary()}

      {isSignatureRequired && (
        <Button link size="small" onClick={handleOpenCoverage} sx={{ mt: 0.5, px: 0 }}>
          View workers
        </Button>
      )}

      <ComplianceAudienceCoverageModal
        open={isModalOpen}
        title={`${complianceName || 'Document'} v.${version}`}
        statusLabel={documentStatusView.label}
        statusColor={documentStatusView.color}
        loading={loading}
        isLoadingMore={isLoadingMore}
        showStatuses={documentStatusView.shouldLoadStatuses}
        infoMessage={documentStatusView.infoMessage}
        coverageEntries={coverageEntries}
        hasMoreCoverageEntries={hasMoreCoverageEntries}
        onClose={() => setModalOpen(false)}
        onLoadMore={loadMoreCoverage}
      />
    </>
  );
};

export default ComplianceDocumentTargetAudienceField;
